/*
  <hv-product-form>: panel de compra de la página de producto.
  Opciones de 1/3/6 frascos + suscripción o compra única. Sin opciones
  (kits y cuidado capilar) vende una pieza al precio del producto. Mantiene en el form
  los campos quantity y selling_plan, y agrega con /cart/add.js (HV.cart), así
  Shopify publica product_added_to_cart igual que el formulario original.
  Al agregar 1 frasco en compra única ofrece cambiar a la opción destacada.
*/
(() => {
  if (customElements.get('hv-product-form')) return;

  const DAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

  class HvProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-hv-product-form]');
      if (!this.form) return;
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      this.modes = Array.from(this.querySelectorAll('[data-mode-option]'));
      this.qtyInput = this.form.querySelector('[data-qty-input]');
      this.planInput = this.form.querySelector('[data-plan-input]');
      this.button = this.form.querySelector('[type="submit"]');
      this.ctaLabel = this.form.querySelector('[data-cta-label]');
      this.msi = this.querySelector('[data-msi]');
      this.dialog = this.querySelector('[data-upsell-dialog]');
      this.section = this.closest('section');
      this.sticky = this.section && this.section.querySelector('hv-sticky-bar');

      this.tiers.forEach((tier) => tier.querySelector('input').addEventListener('change', () => this.update()));
      this.variants = Array.from(this.querySelectorAll('[data-variant]'));
      this.variants.forEach((option) =>
        option.querySelector('input').addEventListener('change', () => this.selectVariant(option))
      );
      this.modes.forEach((mode) => mode.querySelector('input').addEventListener('change', () => this.update()));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      if (this.sticky) {
        const stickyButton = this.sticky.querySelector('[data-sticky-buy]');
        if (stickyButton) stickyButton.addEventListener('click', () => this.form.requestSubmit(this.button));
      }
      if (this.dialog) this.bindUpsell();
      this.renderShipping();
      this.update();
    }

    get tier() {
      return this.tiers.find((tier) => tier.querySelector('input').checked) || this.tiers[0] || this.single;
    }

    // Producto de una pieza: se comporta como una opción fija de 1.
    get single() {
      if (!this._single) {
        const option = this.variants && this.variants.find((item) => item.querySelector('input').checked);
        const title = option ? option.dataset.title : '';
        this._single = {
          dataset: { qty: '1', once: this.dataset.price, sub: '0', compare: this.dataset.compare, title, gift: '0' },
          querySelector: () => null,
        };
      }
      return this._single;
    }

    get mode() {
      const checked = this.modes.find((mode) => mode.querySelector('input').checked);
      return checked ? checked.dataset.modeOption : 'once';
    }

    totals(tier, mode) {
      const sub = mode === 'sub' && Number(tier.dataset.sub) > 0;
      return {
        sub,
        total: Number(sub ? tier.dataset.sub : tier.dataset.once),
        compare: Number(tier.dataset.compare),
      };
    }

    noteFor(tier, mode) {
      const { total, compare } = this.totals(tier, mode);
      const freeShip = Number(this.dataset.freeShip) || 0;
      const saving = compare - total;
      if (freeShip && total >= freeShip) {
        if (saving > 0) return { text: this.dataset.labelSave.replace('[ahorro]', HV.money(saving)), saving: true };
        return { text: this.dataset.labelFree, saving: true };
      }
      return { text: tier.dataset.subtitle, saving: false };
    }

    update() {
      const tier = this.tier;
      const mode = this.mode;
      this.dataset.mode = mode;

      this.tiers.forEach((item) => {
        const selected = item === tier;
        item.classList.toggle('is-selected', selected);
        const { total } = this.totals(item, mode);
        item.querySelector('[data-price]').textContent = HV.money(total);
        const note = this.noteFor(item, mode);
        const noteEl = item.querySelector('[data-note]');
        noteEl.textContent = note.text;
        noteEl.classList.toggle('is-saving', note.saving);
      });
      this.modes.forEach((item) => item.classList.toggle('is-selected', item.dataset.modeOption === mode));

      // Suscripción: plan del tamaño elegido y ahorro contra el precio de lista
      const planLabel = this.querySelector('[data-plan-label]');
      if (planLabel) planLabel.textContent = tier.dataset.planName || '';
      const badge = this.querySelector('[data-sub-badge]');
      if (badge) {
        const sub = Number(tier.dataset.sub);
        const compare = Number(tier.dataset.compare);
        const pct = sub && compare ? Math.round((1 - sub / compare) * 100) : 0;
        badge.textContent = pct > 0 ? `Ahorra ${pct}%` : '';
      }

      const qty = Number(tier.dataset.qty) || 1;
      const { sub, total } = this.totals(tier, mode);
      this.qtyInput.value = qty;
      if (this.planInput) {
        this.planInput.value = sub ? tier.dataset.plan : '';
        this.planInput.disabled = !sub;
      }

      if (this.ctaLabel && this.button && !this.button.disabled) {
        const template = this.dataset.preorder === 'true' ? this.dataset.labelPreorder : this.dataset.labelCta;
        this.ctaLabel.textContent = template.replace('[total]', HV.money(total));
      }

      // Meses sin intereses solo en compra única; abajo del mínimo, texto alterno (Kueski)
      if (this.msi) {
        const months = !sub && total >= Number(this.dataset.msi6) ? 6 : !sub && total >= Number(this.dataset.msi3) ? 3 : 0;
        const fallback = !sub && !months ? this.dataset.msiFallback : '';
        this.msi.hidden = (!months && !fallback) || (this.button && this.button.disabled);
        if (months) {
          const monthly = Math.ceil(total / months / 100) * 100;
          this.msi.querySelector('span').textContent = `o ${months} meses sin intereses de ${HV.money(monthly)}`;
        } else if (fallback) {
          this.msi.querySelector('span').textContent = fallback;
        }
      }

      if (this.sticky) {
        const totalEl = this.sticky.querySelector('[data-sticky-total]');
        const noteEl = this.sticky.querySelector('[data-sticky-note]');
        if (totalEl) totalEl.textContent = tier.dataset.title ? `${tier.dataset.title}, ${HV.money(total)}` : HV.money(total);
        if (noteEl) {
          const gift = Number(tier.dataset.gift);
          const giftRow = tier.querySelector('.hv-tier__gift-title');
          const giftActive = !sub && gift > 0 && giftRow && !tier.querySelector('[data-hv-countdown-scope][hidden]');
          noteEl.textContent = giftActive
            ? `Con ${giftRow.textContent.replace(/^\+/, '').replace(' de regalo', '')} de regalo`
            : this.noteFor(tier, mode).saving
            ? this.noteFor(tier, mode).text
            : '';
        }
      }
    }

    // Cambia la variante (ej. bundle de 3 o 6 frascos): precio, botón y URL.
    selectVariant(option) {
      const { variant, price, compare, available } = option.dataset;
      this.form.querySelector('[name="id"]').value = variant;
      this.dataset.price = price;
      this.dataset.compare = compare;
      this._single = null;
      this.variants.forEach((item) => item.classList.toggle('is-selected', item === option));

      const priceNow = this.querySelector('.hv-pdp__price-now');
      if (priceNow) priceNow.textContent = HV.money(Number(price));
      const priceWas = this.querySelector('.hv-pdp__price-was');
      if (priceWas) {
        priceWas.hidden = !(Number(compare) > Number(price));
        priceWas.querySelector('[data-price-was]').textContent = HV.money(Number(compare));
      }

      const soldOut = available !== 'true';
      this.button.disabled = soldOut;
      if (soldOut && this.ctaLabel) this.ctaLabel.textContent = 'Agotado';
      if (this.sticky) {
        const stickyButton = this.sticky.querySelector('[data-sticky-buy]');
        if (stickyButton) stickyButton.disabled = soldOut;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant);
      window.history.replaceState({}, '', url);
      this.update();
    }

    renderShipping() {
      if (this.dataset.preorder === 'true') return;
      const target = this.querySelector('[data-ship-text]');
      const days = Number(this.dataset.shipDays) || 0;
      if (!target || !days) return;
      const date = new Date();
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) added += 1;
      }
      target.textContent = `Pide hoy y recíbelo el ${DAYS[date.getDay()]}`;
    }

    item(quantity) {
      const item = { id: Number(this.form.querySelector('[name="id"]').value), quantity };
      if (this.planInput && !this.planInput.disabled && this.planInput.value) item.selling_plan = Number(this.planInput.value);
      return item;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!window.HV || !HV.cart) return this.form.submit();
      const qty = Number(this.qtyInput.value) || 1;
      const offer = this.dialog && qty === 1 && this.mode === 'once' && this.dataset.upsell === 'true';
      this.button.classList.add('is-loading');
      try {
        const data = await HV.cart.add([this.item(qty)], this.button, { open: !offer });
        if (offer) {
          this.pending = data;
          this.dialog.showModal();
        }
      } catch (error) {
        this.button.setAttribute('title', error.message);
      } finally {
        this.button.classList.remove('is-loading');
      }
    }

    bindUpsell() {
      const accept = this.dialog.querySelector('[data-upsell-accept]');
      this.dialog.addEventListener('close', () => {
        if (this.pending) HV.cart.open(this.pending, this.button);
        this.pending = null;
      });
      this.dialog.querySelectorAll('[data-upsell-close]').forEach((button) =>
        button.addEventListener('click', () => this.dialog.close())
      );
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      accept.addEventListener('click', async () => {
        // Agrega la diferencia en la misma línea: se publica como product_added_to_cart.
        const target = Number(this.dialog.dataset.upQty) || 3;
        accept.classList.add('is-loading');
        try {
          const data = await HV.cart.add([this.item(target - 1)], this.button, { open: false });
          this.pending = data;
          const upTier = this.tiers.find((tier) => Number(tier.dataset.qty) === target);
          if (upTier) {
            upTier.querySelector('input').checked = true;
            this.update();
          }
        } catch (error) {
          accept.setAttribute('title', error.message);
        } finally {
          accept.classList.remove('is-loading');
          this.dialog.close();
        }
      });
    }
  }

  customElements.define('hv-product-form', HvProductForm);

  /*
    <hv-bundle>: rutina completa. Suma los productos marcados y los agrega
    juntos en una sola llamada a /cart/add.js.
  */
  class HvBundle extends HTMLElement {
    connectedCallback() {
      this.checks = Array.from(this.querySelectorAll('.hv-bundle__check'));
      this.button = this.querySelector('[data-bundle-add]');
      this.checks.forEach((check) => check.addEventListener('change', () => this.update()));
      if (this.button) this.button.addEventListener('click', () => this.add());
      this.update();
    }

    selected() {
      return this.checks.filter((check) => check.checked);
    }

    update() {
      const picked = this.selected();
      const total = picked.reduce((sum, check) => sum + Number(check.dataset.price), 0);
      const compare = picked.reduce((sum, check) => sum + Number(check.dataset.compare), 0);
      const saving = compare - total;
      const set = (selector, text) => {
        const el = this.querySelector(selector);
        if (el) el.textContent = text;
        return el;
      };
      const label = this.querySelector('[data-bundle-label]');
      if (label) label.textContent = label.dataset.template.replace('[n]', picked.length);
      set('[data-bundle-total]', HV.money(total));
      const compareEl = set('[data-bundle-compare]', HV.money(compare));
      if (compareEl) compareEl.hidden = saving <= 0;
      const save = set('[data-bundle-save]', `Ahorras ${HV.money(saving)}`);
      if (save) save.hidden = saving <= 0;

      const freeShip = Number(this.dataset.freeShip) || 0;
      const ship = this.querySelector('[data-bundle-ship]');
      if (ship) {
        ship.hidden = !freeShip;
        ship.querySelector('span').textContent =
          total >= freeShip ? 'Envío gratis' : `Te faltan ${HV.money(freeShip - total)} para envío gratis`;
      }
      const msi = this.querySelector('[data-bundle-msi]');
      if (msi) {
        const months = total >= Number(this.dataset.msi6) ? 6 : total >= Number(this.dataset.msi3) ? 3 : 0;
        msi.hidden = !months;
        if (months) msi.querySelector('span').textContent = `${months} meses sin intereses de ${HV.money(Math.ceil(total / months / 100) * 100)}`;
      }

      if (this.button) {
        this.button.disabled = !picked.length;
        this.button.textContent = this.dataset.labelCta.replace('[n]', picked.length).replace('[total]', HV.money(total));
      }
    }

    async add() {
      const items = this.selected().map((check) => ({ id: Number(check.dataset.id), quantity: 1 }));
      if (!items.length) return;
      this.button.classList.add('is-loading');
      try {
        await HV.cart.add(items, this.button);
      } catch (error) {
        this.button.setAttribute('title', error.message);
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('hv-bundle')) customElements.define('hv-bundle', HvBundle);

  /* Vista ampliada de la galería: un toque en una foto abre el diálogo en
     esa misma foto; ahí se desliza entre todas y se cierra con la X o Esc. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-hv-zoom-open]');
    if (trigger) {
      const scope = trigger.closest('.shopify-section') || document;
      const dialog = scope.querySelector('[data-hv-zoom]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      dialog.showModal();
      if (window.HV && HV.lock) HV.lock();
      const carousel = dialog.querySelector('hv-carousel');
      const index = Number(trigger.dataset.hvZoomOpen) || 0;
      requestAnimationFrame(() => {
        if (!carousel || !carousel.track) return;
        const offsets = carousel.offsets();
        carousel.track.style.scrollBehavior = 'auto';
        carousel.track.scrollLeft = offsets[index] || 0;
        carousel.track.style.scrollBehavior = '';
        carousel.update();
      });
      return;
    }
    const close = event.target.closest('[data-hv-zoom-close]');
    if (close) close.closest('dialog').close();
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches && event.target.matches('[data-hv-zoom]') && window.HV && HV.unlock) HV.unlock();
    },
    true
  );
})();
